import {
  type ChatQuestion,
  type ReviewRow,
  RECIPIENT,
} from '@/karehero-app/tell-us-once/intake/content';
import {
  AA_SECTION_IDS,
  aaAllQuestions,
  aaQuestions,
  aaSectionName,
  type AaFieldType,
  type AaQuestion,
} from '@/karehero-app/explore/tell-us-once/aaQuestions';

/**
 * The Attendance Allowance claim, asked the way the intake and the calculator
 * ask.
 *
 * PROPOSED. Nothing here is built.
 *
 * The questions are the real ones, read from KareHero's normalised AA schema
 * (the same seed the call-to-claim prototype fills the government PDF from), so
 * this cannot drift from the form we actually have to satisfy. The labels are
 * the DWP's words, which is why they read like a form: that is the input, and
 * the mapping below is the argument about how to ask them.
 */

/** The claim's own bar, in place of the calculator's. */
export const AA_BAR = {
  title: 'Attendance Allowance',
  leave: {
    title: 'Leave the claim?',
    body: 'Your answers are saved. You can pick this up where you left off.',
    confirm: 'Leave',
    stay: 'Keep going',
  },
};

/** What the claim opens on: what the intake and the calculator already hold. */
export const AA_CARRIED: { label: string; rows: ReviewRow[] } = {
  label: `What the claim already has about ${RECIPIENT}`,
  rows: [
    { id: 'name', label: 'Her full name', value: 'Margaret Ellen Doyle' },
    { id: 'dob', label: 'Her date of birth', value: '19 March 1943' },
    {
      id: 'address',
      label: 'Her address',
      value: '14 Brae Street, Edinburgh EH3 6HR',
    },
    {
      id: 'condition',
      label: 'Her conditions',
      value: 'Dementia, reduced mobility',
      from: 'ai',
    },
    {
      id: 'carer',
      label: 'Who helps her',
      value: 'You, about 40 hours a week',
      from: 'ai',
    },
  ],
};

export const AA_START = {
  title: 'Five of these the claim already has',
  description:
    'The rest is about the help she needs, day and night. Stop whenever you like and it keeps what you have answered.',
  primary: 'Start',
  carriedNote: 'From the conversation and the calculator.',
};

/** The paperwork that answers AA's questions, rather than the calculator's. */
export const AA_DOCUMENTS = {
  title: 'Send us the paperwork and answer less',
  description:
    'If you have anything that says how much help she needs, we will read what we can from it and only ask about what is left.',
  examples: [
    {
      icon: 'file-lines',
      label: 'Letters from a doctor or hospital',
      note: 'A diagnosis, a discharge summary, a consultant letter',
    },
    {
      icon: 'heart',
      label: 'Care assessments and care plans',
      note: 'From the council, or from a home',
    },
    {
      icon: 'prescription-bottle',
      label: 'Her medication',
      note: 'A repeat prescription list, or a photograph of the boxes',
    },
    {
      icon: 'sterling-sign',
      label: 'Benefit letters',
      note: 'Anything already in payment, and what it is worth',
    },
  ],
  primary: 'Add documents',
  skip: 'Just ask me the questions',
  note: 'Nothing is read for anything other than filling in this claim.',
};

/**
 * The paperwork offer, as the conversation's first turn.
 *
 * It was a screen before the questions — DocumentsFirstScreen, which the
 * calculator still uses: a title, four example cards, a primary and a skip
 * link. A full screen with a primary button reads as a gate to clear before
 * you are allowed to start, and that is a poor first move in a claim people
 * already find daunting. As a turn it is an offer you answer, like everything
 * after it, and the claim is one surface from here to the review.
 *
 * What the screen was good at has to survive the move. The examples are the
 * thing that sends somebody to a drawer: "paperwork" means nothing to a person
 * holding a letter they have not read properly. So their labels come across as
 * a bubble, read from AA_DOCUMENTS rather than retyped so the two cannot
 * drift. Their notes do not come across — a bubble carrying four labels and
 * four clarifications is the screen again.
 */
/**
 * What they hand over, when they hand something over.
 *
 * Two, not one: the offer asks for anything that says how much help she needs,
 * and the answer to that is usually a couple of things rather than the one
 * document a flow would like to name. A care plan and a prescription list are
 * the two most people can actually lay hands on.
 */
export const AA_PAPERWORK_FILES = ['Care plan.pdf', 'Repeat prescription.pdf'];

export const AA_PAPERWORK: ChatQuestion = {
  id: 'paperwork',
  says: ['Before we get going, have you got any paperwork?'],
  ask: AA_DOCUMENTS.description,
  // The examples as one line. Lower case after the first: they were card
  // headings and this is a sentence.
  hint: `${AA_DOCUMENTS.examples
    .map(({ label }, i) =>
      i === 0 ? label : label[0].toLowerCase() + label.slice(1),
    )
    .join(', ')}.`,
  // Rows, so the turn has no composer: there is nothing to type here, and the
  // two ways on are both taps.
  mode: 'rows',
  asides: [
    // 'Add paperwork' rather than the screen's 'Add documents', because the
    // turn above it asks about paperwork.
    {
      icon: 'camera',
      label: 'Add paperwork',
      uploads: 'document',
      file: AA_PAPERWORK_FILES[0],
    },
    // 'Continue', not the screen's 'Just ask me the questions'. The row is
    // under the offer before anything is handed over and still under it
    // afterwards, and a row that declines the offer is the wrong label once
    // they have taken it. Continue is true in both states.
    { icon: 'chevron-right', label: 'Continue', tone: 'go' },
  ],
  answer: 'Continue',
};

/**
 * Which pattern can ask a question, by the control the form uses for it.
 *
 * A rule rather than a judgement per question: 227 of them, and a rule can be
 * argued with. The names are the Patterns page's own, so a row here points at a
 * screen that exists.
 *
 * The exception the rule cannot cover is `text`. An answer that has to be exact
 * has no orb (a misheard name is a rejected claim) and anything that will not
 * fit a field takes the open one, so the identity and admin sections type and
 * the care sections talk.
 */
const TYPED_SECTIONS = [
  '1-about-you',
  '2-signing-form-for-someone-else',
  '3-2-about-gp',
  '10-how-we-pay-you',
  '11-statement-from-someone-who-knows-you',
  '13-declaration',
];

/** The schema's own placeholder, filled with the person it is about. */
const named = (label: string): string =>
  label.replace(/\{careRecipient\}/g, RECIPIENT);

export const AA_PATTERNS: Record<AaFieldType, string> = {
  boolean: 'Answers as rows',
  select: 'Answers as rows',
  'slider-select': 'Answers as rows',
  'multi-select-box': "Continue when you're done",
  number: 'Typed only',
  date: 'Typed only',
  address: 'Typed only',
  text: 'Talk or type',
  none: 'Nothing to ask',
  'question-table': 'Nothing established',
};

export const aaPattern = (q: AaQuestion, section: string): string => {
  if (q.type === 'text' && TYPED_SECTIONS.includes(section))
    return 'Typed only';
  return AA_PATTERNS[q.type];
};

/** One row per question. The section is the group, so it is not a column. */
const questionRow = (q: AaQuestion & { section: string }): string[] => [
  `${'· '.repeat(q.depth)}${named(q.label).trim()}`,
  q.revealedBy ?? (q.required ? 'Always, and required' : 'Always'),
  aaPattern(q, q.section),
];

/** A section's name as a person would say it, with what it costs to answer. */
const sectionLabel = (section: string, count: number): string =>
  `${aaSectionName(section)} (${count})`;

/**
 * The form by section, for a table that shows one at a time.
 *
 * Grouped rather than one list: seventeen sections and 227 questions is a table
 * whose middle nobody reaches.
 */
export const AA_QUESTION_GROUPS: { label: string; rows: string[][] }[] =
  AA_SECTION_IDS.map((section) => {
    const rows = aaQuestions(section).map((q) =>
      questionRow({ ...q, section }),
    );
    return { label: sectionLabel(section, rows.length), rows };
  }).filter((group) => group.rows.length > 0);

/** How many questions each pattern would have to carry, and one of them. */
export const AA_PATTERN_ROWS: string[][] = Object.values(AA_PATTERNS)
  .filter((name, i, all) => all.indexOf(name) === i)
  .map((name) => {
    const mine = aaAllQuestions().filter(
      (q) => aaPattern(q, q.section) === name,
    );
    return [
      name,
      String(mine.length),
      mine[0] ? named(mine[0].label).trim().slice(0, 90) : '—',
    ];
  })
  .filter((row) => row[1] !== '0');

// The care sections: the gate, then the detail behind it. This is the substance
// of the claim and the part the conversation has to carry, so it is the part
// that gets walked rather than tabulated.
const CARE_SECTIONS = [
  '4-care-needs',
  '5-help-with-care-needs-during-the-day',
  '6-help-with-care-needs-during-the-night',
  '7-help-with-care-needs',
];

/**
 * DWP's label, recast in the third person so the chat can ask the carer about
 * the person she looks after.
 *
 * Mechanical, and only good enough to read the shape by. The form is written to
 * the claimant ("do you need help washing?") and the conversation is with her
 * daughter, so every question has to turn round; doing it with a regex gets the
 * common phrasings and mangles the rest. The real wording is a writing job, and
 * this is not it.
 */
const asAsked = (label: string): string =>
  named(label)
    .trim()
    .replace(/^Please tell us /, 'Tell me ')
    .replace(/^Select if /, 'Which of these: ')
    .replace(/\bDo you\b/g, 'Does she')
    .replace(/\bdo you\b/g, 'does she')
    .replace(/\bDoes your\b/g, 'Does her')
    .replace(/\bHave you\b/g, 'Has she')
    .replace(/\bhave you\b/g, 'has she')
    .replace(/\bAre you\b/g, 'Is she')
    .replace(/\bare you\b/g, 'is she')
    .replace(/\bWere you\b/g, 'Was she')
    .replace(/\bDid you\b/g, 'Did she')
    .replace(/\byou have\b/g, 'she has')
    .replace(/\byou need\b/g, 'she needs')
    .replace(/\byou usually\b/g, 'she usually')
    .replace(/\byourself\b/g, 'herself')
    .replace(/\byour\b/g, 'her')
    .replace(/\byou\b/g, 'she')
    .replace(/\bI have\b/g, 'She has')
    .replace(/\bI need\b/g, 'She needs')
    .replace(/\bmy\b/g, 'her');

const asChat = (q: AaQuestion): ChatQuestion => {
  const pattern = aaPattern(q, 'care');
  const rows = q.options.length > 0 || q.type === 'boolean';
  const options =
    q.type === 'boolean' ? ['Yes', 'No'] : q.options.map((o) => o.trim());
  const multi = q.type === 'multi-select-box';

  return {
    id: q.id,
    ask: asAsked(q.label),
    mode: rows ? 'rows' : 'say',
    ...(rows
      ? {
          options,
          asides: [
            ...options.map((label) => ({ label })),
            ...(multi
              ? [
                  {
                    icon: 'chevron-right',
                    label: "Continue when you're done",
                    tone: 'go' as const,
                  },
                ]
              : []),
          ],
          ...(multi ? { multi: true } : {}),
        }
      : {}),
    // Exact answers lose the orb: a count of minutes is not something to say.
    ...(pattern === 'Typed only' ? { voice: false } : {}),
    answer: options[0] ?? 'She needs help most days',
  } satisfies ChatQuestion;
};

/**
 * The care questions, as the chat would ask them.
 *
 * Every question in those four sections, at every depth, so the repetition is
 * visible: a gate, the ways it is true, then how often and for how long. That
 * shape is the whole of AA and it is the thing a conversation has to survive.
 *
 * The paperwork offer opens it, because handing something over is the one way
 * to be asked fewer of them, and that is worth saying before the asking starts
 * rather than after.
 */
/**
 * The reading, as a conversation of its own.
 *
 * `restart`, because Continue closes the paperwork turn: carrying the offer and
 * the files through would leave a finished subject sitting above a message
 * about it. So the claim clears down and says what it is doing, and the dots
 * under it are the whole of the state — nothing here asks for anything.
 *
 * It was a full screen, which took the conversation away in order to say
 * something about it.
 */
export const AA_PROCESSING: ChatQuestion = {
  id: 'processing',
  // What it is actually doing, in their words rather than ours. "Processing"
  // is a status; reading what you gave me is the thing a person would say, and
  // it names the documents as theirs.
  ask: 'I’m just reading what you’ve given me.',
  restart: true,
  // Nothing to say back. The dots are the answer, and the way on arrives when
  // the reading does.
  mode: 'rows',
  voice: false,
};

export const AA_CHAT: ChatQuestion[] = [
  AA_PAPERWORK,
  AA_PROCESSING,
  ...CARE_SECTIONS.flatMap((section) => aaQuestions(section)).map(asChat),
];

/**
 * Where each section starts in that one conversation, so the flows page can
 * draw a row per section instead of one row 122 frames wide.
 *
 * One question list rather than four, because it is one conversation: a step
 * opened at index 40 has the thirty-nine before it behind it.
 */
export const AA_CHAT_SECTIONS: {
  label: string;
  from: number;
  count: number;
}[] = (() => {
  // Past whatever AA_CHAT opens with — the paperwork offer and the reading —
  // because these are indices into that one list. Counted rather than written
  // down: a number here goes wrong silently the next time a turn is added at
  // the front, which is exactly what adding the reading would have done.
  let from = AA_CHAT.findIndex(
    (q) => q.id === aaQuestions(CARE_SECTIONS[0])[0].id,
  );
  return CARE_SECTIONS.map((section) => {
    const count = aaQuestions(section).length;
    const row = {
      label: section.replace(/^\d+-/, '').replace(/-/g, ' '),
      from,
      count,
    };
    from += count;
    return row;
  });
})();
